import TabPanel from '@/components/TabPanel';
import { getDateUpdateValues } from '@/helpers/forecaster/estimation';
import { useAppSelector } from '@/store/hooks';
import { selectShowReachInForecaster } from '@/store/selectors/currentWhitelabel.selectors';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import BidCpmGuidance from '../components/BidCpmGuidance';
import { getChannelTabs, getCommonTabs } from './ChannelTabs';
import TabButton from './TabButton';

const BASE_TAB_ID = 'estimation-tabs';

const EstimationTabs = ({ forecasterData, children }) => {
  const { control, setValue, watch } = useFormContext();
  const currentChannel = useWatch({ control, name: 'currentChannel' });
  const { channelsDataRequestId } = useAppSelector(state => state.forecaster);
  const showReach = useAppSelector(selectShowReachInForecaster);

  const currentTabs = useMemo(() => {
    const tabsByChannel = getChannelTabs(showReach);
    return tabsByChannel[currentChannel] ?? getCommonTabs(showReach);
  }, [currentChannel, showReach]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'tabIndex') {
        const updateValues = getDateUpdateValues(+value.tabIndex);
        if (!updateValues) return; // TODO: grab the current date range value if getDateUpdateValues returns null
        setValue('dateRange', updateValues.dateRange);
        setValue('daysInRange', updateValues.daysInRange);
      }
    });
    return () => subscription.unsubscribe();
  }, [setValue, watch]);

  return (
    <>
      <Controller
        control={control}
        name='tabIndex'
        defaultValue={0}
        render={({ field }) => (
          <>
            <Tabs
              {...field}
              onChange={(_, eventValue) => field.onChange(eventValue)}
              aria-label='estimation period tabs'
              sx={{
                minHeight: '44px',
                background: '#E9EDEB',
                padding: '4px',
                borderRadius: '6px',
                width: '100%',
                [`& .${tabClasses.root}`]: {
                  padding: '10px 0',
                  justifyContent: 'flex-start',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  minHeight: 'auto',
                  minWidth: 'auto',
                  fontWeight: 700,
                },
                [`& .${tabClasses.selected}`]: {
                  background: 'white',
                },
              }}
              slotProps={{
                indicator: {
                  hidden: true,
                },
              }}
            >
              {currentTabs.map(({ id, name }) => (
                <Tab key={id} value={id} component={TabButton} label={name} {...a11yProps(id, BASE_TAB_ID)} />
              ))}
            </Tabs>
            {children}
            {currentTabs.map(({ Component, id }) => (
              <TabPanel className='w-full h-full' key={id} baseTabId={BASE_TAB_ID} value={+field.value} index={id}>
                <Component
                  data={channelsDataRequestId ? null : forecasterData?.[currentChannel]}
                  currentChannel={currentChannel}
                />
              </TabPanel>
            ))}
            <BidCpmGuidance currentChannel={currentChannel} />
          </>
        )}
      />
    </>
  );
};

EstimationTabs.propTypes = {
  forecasterData: PropTypes.object,
  children: PropTypes.node,
};

export default EstimationTabs;
